/**
 * Format file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  let i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes?.[i];
}

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Column definitions with key and header
 * @returns {string} CSV string
 */
export function convertToCSV(data, columns) {
  if (!data || data?.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns?.map(col => col?.header)?.join(',');
  
  // Create data rows
  const rows = data?.map(item => {
    return columns?.map(col => {
      let value = item?.[col?.key];
      
      // Handle nested objects (JSONB fields)
      if (typeof value === 'object' && value !== null) {
        value = JSON.stringify(value);
      }
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }
      
      // Convert to string and escape quotes
      value = String(value)?.replace(/"/g, '""');
      
      // Wrap in quotes if contains comma, newline, or quotes
      if (value?.includes(',') || value?.includes('\n') || value?.includes('"')) {
        value = `"${value}"`;
      }
      
      return value;
    })?.join(',');
  });
  
  return [headers, ...rows]?.join('\n');
}

/**
 * Parse CSV string to array of objects
 * @param {string} csvString - CSV string to parse
 * @param {Array} columns - Column definitions with key and type
 * @returns {Array} Array of objects
 */
export function parseCSV(csvString, columns) {
  if (!csvString || !csvString?.trim()) {
    return [];
  }

  const lines = csvString?.split('\n')?.filter(line => line?.trim());
  if (lines?.length < 2) {
    return [];
  }

  // Skip header row
  const dataLines = lines?.slice(1);
  
  return dataLines?.map(line => {
    const values = parseCSVLine(line);
    const obj = {};
    
    columns?.forEach((col, index) => {
      let value = values?.[index] || '';
      
      // Parse based on column type
      if (col?.type === 'boolean') {
        value = value?.toLowerCase() === 'true';
      } else if (col?.type === 'integer') {
        value = value ? parseInt(value, 10) : null;
      } else if (col?.type === 'jsonb') {
        try {
          value = value ? JSON.parse(value) : col?.default || {};
        } catch (e) {
          value = col?.default || {};
        }
      } else if (col?.type === 'array') {
        try {
          value = value ? JSON.parse(value) : col?.default || [];
        } catch (e) {
          value = col?.default || [];
        }
      } else {
        value = value || null;
      }
      
      obj[col.key] = value;
    });
    
    return obj;
  });
}

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line to parse
 * @returns {Array} Array of values
 */
function parseCSVLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line?.length; i++) {
    const char = line?.[i];
    const nextChar = line?.[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of value
      values?.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last value
  values?.push(current);
  
  return values;
}

/**
 * Download data as CSV file
 * @param {string} csvString - CSV string to download
 * @param {string} filename - Name of the file
 */
export function downloadCSV(csvString, filename) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link?.setAttribute('href', url);
  link?.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
}

/**
 * Read CSV file from input element
 * @param {File} file - File object from input
 * @returns {Promise<string>} Promise that resolves to CSV string
 */
export function readCSVFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    
    if (!file.name.endsWith('.csv')) {
      reject(new Error('File must be a CSV file'));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}